import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "../lib/api";
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import {
  BarChart3,
  TrendingDown,
  Calendar,
  ShoppingCart,
  CreditCard,
  Package,
  Users,
  ArrowRight,
  RefreshCw,
  TrendingUp,
  Megaphone
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";

const AnalyticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ configured: false });
  const [attributionData, setAttributionData] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [dailyPerf, setDailyPerf] = useState(null);
  const [selectedSource, setSelectedSource] = useState("all");
  const [dateRange, setDateRange] = useState({
    from: subDays(new Date(), 30),
    to: new Date()
  });
  const [pendingDateRange, setPendingDateRange] = useState({
    from: subDays(new Date(), 30),
    to: new Date()
  });
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  useEffect(() => {
    checkStatus();
  }, []);

  useEffect(() => {
    if (status.configured) {
      fetchData();
    }
  }, [status.configured, dateRange, selectedSource]);

  const checkStatus = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API}/analytics-ga4/status`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setStatus(response.data);
    } catch (error) {
      console.error("Error checking GA4 status:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    setDataLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = {
        start_date: format(dateRange.from, "yyyy-MM-dd"),
        end_date: format(dateRange.to, "yyyy-MM-dd")
      };

      const [attrResponse, funnelResponse] = await Promise.all([
        axios.get(`${API}/analytics-ga4/attribution`, {
          params,
          withCredentials: true,
          headers
        }).catch((err) => {
          console.error("Attribution error:", err.response?.data || err);
          return null;
        }),
        axios.get(`${API}/analytics-ga4/funnel`, {
          params: { ...params, source_medium: selectedSource === "all" ? null : selectedSource },
          withCredentials: true,
          headers
        }).catch((err) => {
          console.error("Funnel error:", err.response?.data || err);
          return null;
        })
      ]);

      console.log("Attribution data:", attrResponse?.data);
      console.log("Funnel data:", funnelResponse?.data);

      if (attrResponse?.data) setAttributionData(attrResponse.data);
      if (funnelResponse?.data) setFunnelData(funnelResponse.data);

      // ── Daily performance table ──────────────────────────────────────
      try {
        const dailyRes = await axios.get(`${API}/analytics-ga4/daily-performance`, {
          params,
          withCredentials: true,
          headers,
        });
        if (dailyRes?.data) setDailyPerf(dailyRes.data);
      } catch (e) {
        console.warn("Daily performance not available:", e.response?.data || e);
      }
    } catch (error) {
      console.error("Error fetching GA4 data:", error);
      toast.error("Error al obtener datos de GA4");
    } finally {
      setDataLoading(false);
    }
  };

  const FunnelStep = ({ label, value, percentage, icon: Icon, isLast }) => (
    <div className="flex items-center">
      <div className="flex-1">
        <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
          <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center mx-auto mb-2">
            <Icon className="w-6 h-6 text-red-600" />
          </div>
          <p className="text-2xl font-black text-slate-900">{value?.toLocaleString('es-ES') || 0}</p>
          <p className="text-sm text-slate-500">{label}</p>
          {percentage !== undefined && (
            <Badge className="mt-2 bg-slate-100 text-slate-600">
              {percentage}%
            </Badge>
          )}
        </div>
      </div>
      {!isLast && (
        <div className="px-2 flex flex-col items-center">
          <ArrowRight className="w-5 h-5 text-slate-300" />
          <TrendingDown className="w-4 h-4 text-red-400 mt-1" />
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="analytics-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight text-slate-900">Google Analytics 4</h1>
              <p className="text-slate-600">Atribución por fuente/medio y funnel de conversión</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 flex-wrap" data-testid="analytics-ga4-submenu-links">
            <Button className="bg-red-600 hover:bg-red-700 text-white" asChild data-testid="analytics-ga4-self-link-button">
              <Link to="/analytics">GA4</Link>
            </Button>
            <Button variant="outline" asChild data-testid="analytics-ga4-brands-link-button">
              <Link to="/analytics/brands">Marcas</Link>
            </Button>
            <Button variant="outline" asChild data-testid="analytics-ga4-products-link-button">
              <Link to="/analytics/products">Productos</Link>
            </Button>
            <Popover open={datePopoverOpen} onOpenChange={(open) => { setDatePopoverOpen(open); if (open) setPendingDateRange(dateRange); }}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="border-slate-200">
                  <Calendar className="w-4 h-4 mr-2" />
                  {format(dateRange.from, "d MMM", { locale: es })} - {format(dateRange.to, "d MMM", { locale: es })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <div className="flex">
                  {/* Quick date options */}
                  <div className="border-r border-slate-200 p-2 space-y-1 min-w-[180px]">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-2 py-1">Accesos rápidos</p>
                    {[
                      { label: "Hoy", from: new Date(), to: new Date() },
                      { label: "Ayer", from: subDays(new Date(), 1), to: subDays(new Date(), 1) },
                      { label: "Últimos 7 días", from: subDays(new Date(), 6), to: new Date() },
                      { label: "Últimos 14 días", from: subDays(new Date(), 13), to: new Date() },
                      { label: "Últimos 30 días", from: subDays(new Date(), 29), to: new Date() },
                      { label: "Esta semana", from: startOfWeek(new Date(), { weekStartsOn: 1 }), to: new Date() },
                      { label: "Semana pasada", from: startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }), to: endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }) },
                      { label: "Este mes", from: startOfMonth(new Date()), to: new Date() },
                      { label: "Mes pasado", from: startOfMonth(subMonths(new Date(), 1)), to: endOfMonth(subMonths(new Date(), 1)) },
                    ].map((preset) => (
                      <button
                        key={preset.label}
                        onClick={() => {
                          const range = { from: preset.from, to: preset.to };
                          setDateRange(range);
                          setPendingDateRange(range);
                          setDatePopoverOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                  {/* Calendar */}
                  <div className="p-2 flex flex-col">
                    <CalendarComponent
                      mode="range"
                      selected={pendingDateRange}
                      onSelect={(range) => range && setPendingDateRange(range)}
                      numberOfMonths={2}
                      locale={es}
                    />
                    <div className="border-t border-slate-200 pt-2 mt-1 flex justify-end">
                      <Button
                        size="sm"
                        className="bg-red-600 hover:bg-red-700 text-white"
                        disabled={!pendingDateRange.from}
                        onClick={() => {
                          const range = { from: pendingDateRange.from, to: pendingDateRange.to || pendingDateRange.from };
                          setDateRange(range);
                          setPendingDateRange(range);
                          setDatePopoverOpen(false);
                        }}
                      >
                        Aplicar
                      </Button>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            
            <Button
              variant="outline"
              className="border-slate-200"
              onClick={fetchData}
              disabled={dataLoading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${dataLoading ? 'animate-spin' : ''}`} />
              {dataLoading ? 'Cargando...' : 'Actualizar'}
            </Button>
          </div>
        </div>

        {!status.configured ? (
          <Card className="bg-slate-50 border border-slate-200">
            <CardContent className="p-12 text-center">
              <BarChart3 className="w-16 h-16 text-slate-400 mx-auto mb-4" />
              <h3 className="font-bold text-xl text-slate-900 mb-2">
                Conecta Google Analytics 4
              </h3>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">
                Ve a Integraciones y conecta tu cuenta de Google Analytics para ver datos de atribución y funnel.
              </p>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => window.location.href = "/integrations"}
              >
                Ir a Integraciones
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Funnel Section */}
            <Card className="bg-white border border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-bold text-slate-900">
                  Funnel de Conversión
                </CardTitle>
                {attributionData?.by_source_medium?.length > 0 && (
                  <Select value={selectedSource} onValueChange={setSelectedSource}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Filtrar por fuente" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las fuentes</SelectItem>
                      {attributionData.by_source_medium.map((item) => (
                        <SelectItem key={item.source_medium} value={item.source_medium}>
                          {item.source_medium}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </CardHeader>
              <CardContent>
                {funnelData?.funnel ? (
                  <>
                    <div className="grid grid-cols-4 gap-2">
                      <FunnelStep 
                        label="Sesiones" 
                        value={funnelData.funnel.sessions}
                        icon={Users}
                      />
                      <FunnelStep 
                        label="Añadir al carrito" 
                        value={funnelData.funnel.add_to_cart}
                        percentage={funnelData.funnel.traffic_to_atc_rate}
                        icon={ShoppingCart}
                      />
                      <FunnelStep 
                        label="Checkout" 
                        value={funnelData.funnel.begin_checkout}
                        percentage={funnelData.funnel.atc_to_checkout_rate}
                        icon={CreditCard}
                      />
                      <FunnelStep 
                        label="Compras" 
                        value={funnelData.funnel.purchases}
                        percentage={funnelData.funnel.checkout_to_purchase_rate}
                        icon={Package}
                        isLast
                      />
                    </div>
                    
                    {/* Conversion Rate Summary */}
                    <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                      <div className="grid grid-cols-4 gap-4 text-center">
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider">% Tráfico → ATC</p>
                          <p className="text-xl font-bold text-slate-900">{funnelData.funnel.traffic_to_atc_rate}%</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider">% ATC → Checkout</p>
                          <p className="text-xl font-bold text-slate-900">{funnelData.funnel.atc_to_checkout_rate}%</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider">% Checkout → Compra</p>
                          <p className="text-xl font-bold text-slate-900">{funnelData.funnel.checkout_to_purchase_rate}%</p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-2 -m-2">
                          <p className="text-xs text-red-600 uppercase tracking-wider">% Conversión Total</p>
                          <p className="text-xl font-bold text-red-600">{funnelData.funnel.overall_conversion_rate}%</p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    No hay datos de funnel disponibles
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Attribution Table */}
            <Card className="bg-white border border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-bold text-slate-900">
                  Atribución por Fuente/Medio
                </CardTitle>
              </CardHeader>
              <CardContent>
                {attributionData?.by_source_medium?.length > 0 ? (
                  <div className="space-y-0">
                    {/* Header Row */}
                    <div className="grid grid-cols-5 gap-4 py-3 px-4 border-b-2 border-slate-200 bg-slate-50 rounded-t-lg">
                      <div className="font-semibold text-slate-600 text-sm">Fuente / Medio</div>
                      <div className="font-semibold text-slate-600 text-sm text-right">Sesiones</div>
                      <div className="font-semibold text-slate-600 text-sm text-right">Transacciones</div>
                      <div className="font-semibold text-slate-600 text-sm text-right">Ingresos (GA4)</div>
                      <div className="font-semibold text-slate-600 text-sm text-right">% Conversión</div>
                    </div>
                    {/* Data Rows */}
                    {attributionData.by_source_medium.map((row, idx) => (
                      <div 
                        key={row.source_medium || idx} 
                        className="grid grid-cols-5 gap-4 py-3 px-4 border-b border-slate-100 hover:bg-slate-50 transition-colors"
                      >
                        <div className="font-medium text-slate-900 truncate">
                          {row.source_medium || '-'}
                        </div>
                        <div className="text-right font-mono text-slate-700">
                          {(row.sessions || 0).toLocaleString('es-ES')}
                        </div>
                        <div className="text-right font-mono text-slate-700">
                          {(row.transactions || 0).toLocaleString('es-ES')}
                        </div>
                        <div className="text-right font-mono text-slate-700">
                          €{(row.revenue || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })}
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            (row.conversion_rate || 0) >= 3 
                              ? "bg-green-100 text-green-700" 
                              : (row.conversion_rate || 0) >= 1 
                                ? "bg-amber-100 text-amber-700"
                                : "bg-slate-100 text-slate-600"
                          }`}>
                            {(row.conversion_rate || 0).toFixed(2)}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    {dataLoading ? 'Cargando datos...' : 'No hay datos de atribución disponibles'}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Daily Performance Table */}
            {dailyPerf && (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Megaphone className="w-4 h-4 text-slate-500" />
                      <CardTitle className="text-base font-semibold text-slate-800">
                        Rendimiento diario — Ingresos vs Publicidad
                      </CardTitle>
                    </div>
                    {!dailyPerf.has_ads_data && (
                      <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50 text-xs">
                        Sin datos de anuncios
                      </Badge>
                    )}
                    {dailyPerf.has_ads_data && dailyPerf.platforms?.length > 0 && (
                      <Badge variant="outline" className="text-slate-500 border-slate-200 text-xs">
                        {dailyPerf.platforms.map(p => ({
                          google_ads: "Google Ads",
                          meta_ads: "Meta Ads",
                          awin: "Awin",
                          tiktok_ads: "TikTok Ads",
                        }[p] || p)).join(" · ")}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {/* Table header */}
                  <div className="grid grid-cols-4 gap-2 px-4 py-2 bg-slate-50 border-b border-slate-100 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                    <div>Fecha</div>
                    <div className="text-right">Ingresos válidos</div>
                    <div className="text-right">Coste Ads</div>
                    <div className="text-right">% Ads</div>
                  </div>

                  {/* Rows */}
                  <div className="divide-y divide-slate-50 max-h-96 overflow-y-auto">
                    {dailyPerf.rows
                      .filter(r => r.revenue > 0 || r.ads_cost > 0)
                      .sort((a, b) => b.date.localeCompare(a.date))
                      .map((row) => {
                        const pct = row.ads_pct;
                        const pctColor =
                          pct === null ? "text-slate-300" :
                          pct > 30 ? "text-red-600 bg-red-50" :
                          pct > 20 ? "text-amber-600 bg-amber-50" :
                          "text-green-700 bg-green-50";
                        return (
                          <div
                            key={row.date}
                            className="grid grid-cols-4 gap-2 px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors"
                            data-testid={`daily-row-${row.date}`}
                          >
                            <div className="text-slate-700 font-medium">
                              {format(parseISO(row.date), "d MMM", { locale: es })}
                              <span className="text-slate-300 text-xs ml-1">
                                {format(parseISO(row.date), "EEE", { locale: es })}
                              </span>
                            </div>
                            <div className="text-right font-mono text-slate-800">
                              {row.revenue > 0
                                ? `€${row.revenue.toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                                : <span className="text-slate-300">—</span>}
                            </div>
                            <div className="text-right font-mono text-slate-600">
                              {row.ads_cost > 0
                                ? `€${row.ads_cost.toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                                : <span className="text-slate-300">—</span>}
                            </div>
                            <div className="text-right">
                              {pct !== null && row.ads_cost > 0 ? (
                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${pctColor}`}>
                                  {pct.toFixed(1)}%
                                </span>
                              ) : (
                                <span className="text-slate-300 text-xs">—</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>

                  {/* Totals footer */}
                  {(() => {
                    const filtered = dailyPerf.rows.filter(r => r.revenue > 0 || r.ads_cost > 0);
                    const totalRev = filtered.reduce((s, r) => s + r.revenue, 0);
                    const totalAds = filtered.reduce((s, r) => s + r.ads_cost, 0);
                    const totalPct = totalRev > 0 ? (totalAds / totalRev * 100) : null;
                    return (
                      <div className="grid grid-cols-4 gap-2 px-4 py-2.5 border-t border-slate-200 bg-slate-50 text-sm font-semibold">
                        <div className="text-slate-500 text-xs uppercase tracking-wide">Total</div>
                        <div className="text-right font-mono text-slate-900">
                          €{totalRev.toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </div>
                        <div className="text-right font-mono text-slate-700">
                          €{totalAds.toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </div>
                        <div className="text-right">
                          {totalPct !== null && totalAds > 0 ? (
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                              totalPct > 30 ? "text-red-600 bg-red-50" :
                              totalPct > 20 ? "text-amber-600 bg-amber-50" :
                              "text-green-700 bg-green-50"
                            }`}>
                              {totalPct.toFixed(1)}%
                            </span>
                          ) : "—"}
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            )}

            {/* Info Card */}
            <Card className="bg-amber-50 border border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <BarChart3 className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-900">Nota sobre atribución GA4</h4>
                    <p className="text-sm text-amber-700 mt-1">
                      GA4 usa un modelo de atribución basado en datos por defecto. Compara estos datos 
                      con los ingresos reales de PrestaShop para detectar discrepancias de atribución.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AnalyticsPage;
